import { Outlet, Link, useNavigate } from 'react-router-dom';
import { Home, Package, PackagePlus, LogOut, User, DollarSign, Shield, MessageCircle, Settings, Truck } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../store/auth';
import { api } from '../../services/api';

export function Layout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  // Check if user is admin
  const { data: adminCheck } = useQuery({
    queryKey: ['adminCheck'],
    queryFn: () => api.admin.checkAdmin(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Check for unread Discogs messages
  const { data: unreadData } = useQuery({
    queryKey: ['discogs-unread-count'],
    queryFn: () => api.discogs.getUnreadCount(),
    staleTime: 60 * 1000, // Refresh every minute
    refetchInterval: 60 * 1000, // Poll every minute
  });

  const isAdmin = adminCheck?.isAdmin ?? false;
  const unreadCount = unreadData?.count ?? 0;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Header - sticky so it doesn't scroll away */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2">
              <Package className="w-8 h-8 text-primary-600" />
              <span className="text-xl font-bold text-gray-900">findmystuff</span>
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link
                to="/dashboard"
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <Home className="w-4 h-4" />
                Dashboard
              </Link>
              <Link
                to="/selling"
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <DollarSign className="w-4 h-4" />
                Selling
              </Link>
              <Link
                to="/selling/shipping"
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <Truck className="w-4 h-4" />
                Shipping
              </Link>
              <Link
                to="/selling/messages"
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <div className="relative">
                  <MessageCircle className="w-4 h-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-semibold rounded-full flex items-center justify-center ring-2 ring-white">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </div>
                Messages
              </Link>
              <Link
                to="/settings"
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <Settings className="w-4 h-4" />
                Settings
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="flex items-center gap-2 text-purple-600 hover:text-purple-700"
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </Link>
              )}
              <Link
                to="/add-inventory"
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                <PackagePlus className="w-4 h-4" />
                Add Inventory
              </Link>
            </nav>

            {/* User menu */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <User className="w-4 h-4" />
                {user?.displayName}
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 md:pb-8">
        <Outlet />
      </main>

      {/* Mobile navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200">
        <div className="flex justify-around py-2">
          <Link
            to="/dashboard"
            className="flex flex-col items-center gap-1 p-2 text-gray-600"
          >
            <Home className="w-5 h-5" />
            <span className="text-xs">Home</span>
          </Link>
          <Link
            to="/add-inventory"
            className="flex flex-col items-center gap-1 p-2 text-primary-600"
          >
            <PackagePlus className="w-5 h-5" />
            <span className="text-xs">Add</span>
          </Link>
          <Link
            to="/selling"
            className="flex flex-col items-center gap-1 p-2 text-gray-600"
          >
            <DollarSign className="w-5 h-5" />
            <span className="text-xs">Selling</span>
          </Link>
          <Link
            to="/selling/messages"
            className="flex flex-col items-center gap-1 p-2 text-gray-600"
          >
            <div className="relative">
              <MessageCircle className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-semibold rounded-full flex items-center justify-center ring-2 ring-white shadow-sm">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <span className="text-xs">Messages</span>
          </Link>
          {isAdmin ? (
            <Link
              to="/admin"
              className="flex flex-col items-center gap-1 p-2 text-purple-600"
            >
              <Shield className="w-5 h-5" />
              <span className="text-xs">Admin</span>
            </Link>
          ) : (
            <button
              onClick={handleLogout}
              className="flex flex-col items-center gap-1 p-2 text-gray-600"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-xs">Logout</span>
            </button>
          )}
        </div>
      </nav>
    </div>
  );
}
